import { Injectable } from "@angular/core";
import { ProductDetailsDataModel3, ProductDataModel4, ProductDetailViewModel, MenuSettingsDataModel2, MenuSettingViewModel, ProductExtraViewModel, ProductPricesModel, UsingType, ItemTagViewModel } from "../../shared/models";
import { CircularInjectionHelper } from "../../services/circularInjectionHelper";
import { ContentType, ImageOrientation, DateFormatTyps, SortDirection, WebShopMode, VoucherServiceType } from "../../shared/contentTypes";
import { Observable } from "rxjs";
import { CoreLogService } from "../../services/log.service";
import { RatingService } from "../../services/rating.service";
import { LikeService } from "../../services/like.service";
import { ProductDetailsHelper, ProductDetailsHelperService } from "../../services/product_details_helper.service";
import { CoreLanguageService } from "../../services/language.service";
import * as _ from "lodash";
import DateManager from "../../shared/dateManager";
import { PagingHelper } from "../../shared/pagingHelper";
import { SortOption } from "../../shared/models/sort.model";
import Utils from "../../shared/utils";
import { WebShopService } from "../../services/webShop.service";
import { ProductService } from "../../services/product.service";

export enum MenuTab {
  Overview = 1,
  CO2,
  Additives,
  Allergens,
  ECommerce,
  Nutritions,
}

@Injectable()
export class MenudetailService {

  private pagingHelper: PagingHelper = new PagingHelper();
  private extraItemsSortOption: SortOption[] = [];

  constructor(private _objObsInjectHelper: CircularInjectionHelper
    , private _coreLogService: CoreLogService
    , private _ratingService: RatingService
    , private _likeService: LikeService
    , private _productService: ProductService
    , private _webshopService: WebShopService
    , private _coreLanguageService: CoreLanguageService) {
    const me = this;
    me.extraItemsSortOption.push(me.pagingHelper.createSortOption("order", SortDirection.ASC));
  }

  public get ProductObserveChanges(): Observable<void> {
    return this._productService.ProductChanges;
  }

  public get MenuSettingObserveChanges(): Observable<void> {
    return this._productService.MenuSettingsChangedChanges;
  }

  public get serviceModeChargeWebvoucher(): boolean {
    const me = this;
    return me._webshopService.voucherServiceType === VoucherServiceType.Charge
  }

  public GetProductDetailById(location: number, id: number, itemTags: ItemTagViewModel[] = null): Promise<ProductDetailViewModel> {
    const me = this;
    return new Promise<ProductDetailViewModel>((resolve, reject) => {
      me._productService.GetProduct(location, me._webshopService.sortiment, id)
        .then(product => {
          me.mapToProductDetailViewModel(location, product, itemTags)
            .then(resolve)
            .catch(e => {
              me._coreLogService.error("MenudetailService.GetProductDetailById", "mapToProductDetailViewModel", e);
              reject();
            });
        })
        .catch(e => {
          me._coreLogService.error("MenudetailService.GetProductDetailById", "GetObjectsByFilter", e);
          reject();
        });
    });
  }

  /** ermittelt den Artikel und die Details zu einer ProductID */
  public async GetProductDataByProductId(productId: number, withProductDetails: boolean = true): Promise<{ product: ProductDataModel4, productDetails: ProductDetailsDataModel3 }> {
    const me = this;
    const products = await me._productService.GetProductsByFilter(me._webshopService.locationID, me._webshopService.sortiment, (product) => product.product_id == productId);
    if (products.length == 0) {
      me._coreLogService.error("MenudetailService.GetProductDataByProductId", "GetProductsByFilter no match", location, me._webshopService.sortiment, productId);
      return null;
    }
    const product = products[0];
    if (!withProductDetails) {
      return { product, productDetails: null };
    }
    const productDetails = await ProductDetailsHelperService.GetProductDetails(product, me._objObsInjectHelper, true, false);
    if (productDetails == null) {
      me._coreLogService.error("MenudetailService.GetProductDataByProductId", "GetProductDetails no match", location, me._webshopService.sortiment, productId);
      return null;
    }
    return { product, productDetails };
  }

  private async mapToProductDetailViewModel(location: number, model: ProductDataModel4, itemTags: ItemTagViewModel[] = null): Promise<ProductDetailViewModel> {
    const me = this;
    if (model) {
      const viewModel = new ProductDetailViewModel();
      viewModel.id = model.id;
      viewModel.productId = model.product_id;
      if (me.serviceModeChargeWebvoucher) {
        viewModel.labelDE = "GUTHABEN AUFLADEN";
        viewModel.labelEN = "CHARGE CREDIT";
        viewModel.imagePhoto = await me.loadChargeVoucherImage();
      } else {
        viewModel.labelDE = model.label_de;
        viewModel.labelEN = model.label_en;
        if (model.last_image_update_at_hms) {
          viewModel.imagePhoto = ContentType.sub_Product + "/" + model.product_id + "_photo_" + ImageOrientation.Landscape + "/" + model.last_image_update_at_ymd + "/" + model.last_image_update_at_hms;
        } else {
          viewModel.imagePhoto = "";
        }
      }
      viewModel.price = model.extras;
      viewModel.usingtype = model.usingtype;

      try {
        viewModel.rate = await me._ratingService.GetRatingStars(ContentType.sub_Product, model.product_id);
      } catch (error) {
        me._coreLogService.error("MenudetailService.mapToProductViewModel", "GetRatingStars try catch", error);
      }
      try {
        viewModel.like = await me._likeService.HasLike(ContentType.sub_Product, model.product_id);
      } catch (error) {
        me._coreLogService.error("MenudetailService.mapToProductViewModel", "HasLike try catch", error);
      }
      try {
        const productDetail = await ProductDetailsHelperService.GetProductDetails(model, me._objObsInjectHelper, true, false);
        const prices = await me._webshopService.getProductPrices(model, true, productDetail);
        viewModel.price = prices.discountPriceWithExtras;
        const menuSettings = await me.GetMenuSettings();
        let showAdditionalInformation = false;
        viewModel.basePrice = prices.discountPrice;
        viewModel.deposit = prices.deposit;
        viewModel.use_eCommerce = menuSettings.use_eCommerce;
        if (viewModel.use_eCommerce) {
          showAdditionalInformation = true;
          viewModel.item_content_de = productDetail.item_content;
          viewModel.item_content_en = productDetail.item_content_en;

          viewModel.item_base_unit = productDetail.item_base_unit_id;
          viewModel.item_base_factor = productDetail.item_base_factor;
          viewModel.item_caffeine = productDetail.item_caffeine;
          viewModel.item_alcohol = productDetail.item_alcohol;

          viewModel.item_provider_name_en = productDetail.item_provider_name_en;
          viewModel.item_provider_name_de = productDetail.item_provider_name;
          viewModel.item_provider_addr_de = productDetail.item_provider_addr;
          viewModel.item_provider_addr_en = productDetail.item_provider_addr_en;
          viewModel.prepackaged = productDetail.prepackaged;
        }
        if (menuSettings.use_nutrition_values) {
          showAdditionalInformation = true;
          viewModel.nutritions = ProductDetailsHelper.getNutritionsViewModel(productDetail, true, true);
        }
        if (menuSettings.use_app_description) {
          viewModel.description_de = productDetail.details_de;
          viewModel.description_en = productDetail.details_en;
        }
        if (menuSettings.use_additives || menuSettings.use_allergens || menuSettings.use_co2) {
          showAdditionalInformation = true;
        }
        viewModel.productHeaderGroups = me.loadProductHeaderGroups(menuSettings, model, productDetail);
        viewModel.productExtras = await me.loadProductExtras$(productDetail, prices);
        // erweiterung der Extras um die AdditionalInformation
        if (showAdditionalInformation && viewModel.productExtras) {
          // asyncrones parallels laden aller Extras, für performance
          for (let i = 0; i < viewModel.productExtras.length; i++) {
            const extra = viewModel.productExtras[i];
            if (extra.productExtraItems) {
              for (let j = 0; j < extra.productExtraItems.length; j++) {
                const extraItem = extra.productExtraItems[j];
                me.GetProductDataByProductId(extraItem.product_id).then((productData) => {
                  if (productData) {
                    extraItem.initAdditionalInfo(productData.productDetails, menuSettings);
                  } else {
                    // keine AdditionalInformation notwendig, da kein Produkt gefunden
                    me._coreLogService.error("MenudetailService.mapToProductViewModel", "GetProductDataByProductId no match", location, me._webshopService.sortiment, extraItem.product_id);
                  }
                });
              }
            }
          }
        }
      } catch (error) {
        me._coreLogService.error("MenudetailService.mapToProductViewModel", "try catch", error);
      }
      try {
        const lastSyncData = await me._productService.GetProductLastSyncAt(location, me._webshopService.sortiment);
        viewModel.lastSynceData = DateManager.MomentAsString(lastSyncData, DateFormatTyps.DDMMYYYY);
      } catch (error) {
        me._coreLogService.error("MenudetailService.mapToProductViewModel", "GetLastSyncAt try catch", error);
      }
      if (model.tag_ids && model.tag_ids.length > 0 && itemTags) {
        viewModel.itemTags = [];
        viewModel.itemTags.push(...itemTags.filter(t => model.tag_ids.includes(t.id)));
      }
      return Promise.resolve(viewModel);
    } else {
      return Promise.resolve(null);
    }
  }

  /** ermittelt die MenuSettings  */
  public async GetMenuSettings(): Promise<MenuSettingsDataModel2> {
    return this._productService.GetMenuSettings();
  }

  private async loadProductExtras$(details: ProductDetailsDataModel3, prices: ProductPricesModel,): Promise<ProductExtraViewModel[]> {
    const me = this;
    const productExtraViewModels = ProductDetailsHelper.createProductExtras(me._webshopService.sortiment, details, prices);
    await Utils.asyncForEach(productExtraViewModels, async (productExtraViewModel) => {
      await Utils.asyncForEach(productExtraViewModel.productExtraItems, async (item) => {
        item.isAvailable = await me._webshopService.isProductAvailable$(item.product_id)
      });
      productExtraViewModel.productExtraItems.sort(me.pagingHelper.fieldSorter(me.extraItemsSortOption));
    });
    return productExtraViewModels;
  }

  private loadProductHeaderGroups(menuSettings: MenuSettingsDataModel2, product: ProductDataModel4, productDetails: ProductDetailsDataModel3): MenuSettingViewModel[] {
    const me = this;
    const menuSettingViewModels: MenuSettingViewModel[] = [];
    const isWebvoucher = product.usingtype === UsingType.Webvoucher;
    if (menuSettings.use_app_description) {
      menuSettingViewModels.push(me.loadMenuSettingItem(MenuTab.Overview, Utils.decodeHTMLEntities(productDetails.details_de), Utils.decodeHTMLEntities(productDetails.details_en), "phProductDetail_Overview"));
    } else {
      menuSettingViewModels.push(me.loadMenuSettingItem(MenuTab.Overview, "", "", "phProductDetail_Overview"));
    }
    if (menuSettings.use_co2 && !isWebvoucher) {
      menuSettingViewModels.push(me.loadMenuSettingItem(MenuTab.CO2, (productDetails.co2).toString(), (productDetails.co2).toString(), "phProductDetail_CO2"));
    }
    if (menuSettings.use_additives && !isWebvoucher) {
      const keys = ProductDetailsHelper.extractAdditivesAndAllergiesKeysFromText(productDetails.additives_en);
      if (keys.length > 0) {
        menuSettingViewModels.push(me.loadMenuSettingItem(
          MenuTab.Additives,
          me.setContentHtml(me.getLanguageTextFromLabels(keys, "de")),
          me.setContentHtml(me.getLanguageTextFromLabels(keys, "en")),
          "phProductDetail_Additives"
        ));
      }
    }
    if (menuSettings.use_allergens && !isWebvoucher) {
      const keys = ProductDetailsHelper.extractAdditivesAndAllergiesKeysFromText(productDetails.allergens_en);
      if (keys.length > 0) {
        menuSettingViewModels.push(me.loadMenuSettingItem(
          MenuTab.Allergens,
          me.setContentHtml(me.getLanguageTextFromLabels(keys, "de")),
          me.setContentHtml(me.getLanguageTextFromLabels(keys, "en")),
          "phProductDetail_Allergy"
        ));
      }
    }
    if (menuSettings.use_eCommerce && !isWebvoucher) {
      if (!Utils.IsNullOrEmpty(productDetails.item_provider_addr) || !Utils.IsNullOrEmpty(productDetails.item_provider_name) || !Utils.IsNullOrEmpty(productDetails.item_content)) {
        menuSettingViewModels.push(me.loadMenuSettingItem(MenuTab.ECommerce, "", "", ""));
      }
    }
    if (menuSettings.use_nutrition_values && !isWebvoucher) {
      menuSettingViewModels.push(me.loadMenuSettingItem(MenuTab.Nutritions, "", "", "phProductDetail_Nutrition"));
    }
    return menuSettingViewModels;
  }

  private loadMenuSettingItem(id: number, contentDE: string, contentEN: string, labelKey: string): MenuSettingViewModel {
    const me = this;
    const menuSettingViewModel = new MenuSettingViewModel();
    menuSettingViewModel.id = id;
    if (me._webshopService.getServiceMode() === WebShopMode.Webvoucher && me._webshopService.voucherServiceType === VoucherServiceType.Charge) {
      menuSettingViewModel.labelDE = "GUTHABEN AUFLADEN";
      menuSettingViewModel.labelEN = "CHARGE CREDIT";
      menuSettingViewModel.contentDE = "Lade jetzt dein Guthaben über deinen Wunschbetrag auf.<br>Gutscheine sind in allen teilnehmenden Standorten einlösbar.";
      menuSettingViewModel.contentEN = "Charge your credit now over your desired amount. Vouchers can be redeemed in all participating locations.";
    } else {
      const currentLang = me._coreLanguageService.getCurrentLanguage();
      me._coreLanguageService.setCurrentLanguage("de");
      menuSettingViewModel.labelDE = me._coreLanguageService.value(labelKey) || labelKey;
      me._coreLanguageService.setCurrentLanguage("en");
      menuSettingViewModel.labelEN = me._coreLanguageService.value(labelKey) || labelKey;
      me._coreLanguageService.setCurrentLanguage(currentLang);

      menuSettingViewModel.contentDE = contentDE;
      menuSettingViewModel.contentEN = contentEN;
    }
    return menuSettingViewModel;
  }

  /** display the "i" button if the required settings are active  and content is included
   * @returns canDisplayInformationBtn
   */
  public displayAdditionalInformation(): boolean {
    const me = this;
    let canDisplayInformationBtn: boolean = true;// todo true is used to test and has to be changed to false (default)
    return canDisplayInformationBtn;
  }

  /** Builds a string concattenating descriptions for keys
   * Generated descriptions for keys are seperated by newlines
   * @param keys Array containing keys for allergies or additives
   * @param lng "de", "en"
  */
  private getLanguageTextFromLabels(labels: string[], lng: string): string {
    const translations = ProductDetailsHelper.getTranslationsFromAdditivesAndAllergiesKeys(labels, lng);
    return translations.join("\n");;
  }

  private setContentHtml(content: string): string {
    if (content) {
      return `<p>${content.replace(/(\r\n|\r|\n)/g, "</p><p>")}</p>`;
    } else {
      return "";
    }
  }

  private async loadChargeVoucherImage(): Promise<string> {
    const me = this;
    let image: string = "";
    await me._webshopService.getWebshopSettingsDataModel().then((webshopSettings) => {
      if (webshopSettings.webvoucher) {
        if (webshopSettings.webvoucher.charge_last_image_update_at_ymd > 0) {
          image = webshopSettings.webvoucher.charge_image_photo;
        }
      }
    })
    return image;
  }

}
